import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Algebra II, first half: complex numbers, polynomial division and polynomial
 * functions, then the two places where the domain bites back — rational
 * functions and radical equations.
 *
 * The lesson worth getting right is `complex-numbers`. A student meets i as a
 * rule to be obeyed ("i squared is minus one, remember that") and never
 * recovers the sense that anything was gained. It is taught here as an
 * *extension*, in the same series as the one that admitted negative numbers so
 * subtraction always worked and fractions so division always worked — because
 * that is what it is, and because a student who sees the pattern can predict
 * the answer before being told it.
 */
export const ALGEBRA_TWO_LESSONS: Lesson[] = [
  lesson({
    id: 'complex-numbers-01',
    unitId: 'unit-complex-polynomials',
    conceptId: 'complex-numbers',
    title: 'Complex Numbers',
    summary: 'Admitting a square root of −1, and finding that it settles every quadratic at once.',
    objectives: [
      'Write the square root of a negative number using i',
      'Add, subtract and multiply complex numbers',
      'Read the discriminant as a count of real and complex roots',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'A question with no answer, again',
        blocks: [
          text('Solve this:'),
          math('x^{2} + 1 = 0'),
          text('Rearranged, it asks for a number whose square is −1. There is no such number, and you have been told so for years.'),
          text('You have been told that before, though, and it did not last:'),
          table(['The question', 'Had no answer among…', 'So we admitted…'], [
            ['5 − 8 = ?', 'the counting numbers', 'negatives'],
            ['3 ÷ 4 = ?', 'the whole numbers', 'fractions'],
            ['x² = 2', 'the fractions', 'irrationals'],
            ['x² = −1', 'the real numbers', '?'],
          ]),
          text('Each row is the same move made again. This lesson makes it one more time, and it is the last time it is needed.'),
        ],
      },
      intuition: {
        title: 'One new number, and nothing else',
        blocks: [
          text('Define a single new number, called **i**, by the one property that was missing:'),
          math('i^{2} = -1'),
          insight('Nothing else about i is stipulated. It obeys every rule of arithmetic you already know — it can be added, multiplied and collected like a variable. Every fact about complex numbers below follows from ordinary algebra plus that one line.'),
          text('A **complex number** is anything of the form a + bi, with a and b real. a is the *real part* and b the *imaginary part*.'),
          warning('The two parts never merge. 3 + 2i is a finished answer, in the same way "3 metres and 2 seconds" is a finished answer — writing 5i or 5 would be claiming the parts are the same kind of thing. Add real to real and imaginary to imaginary, and stop.', 'Two parts, not one number waiting to be simplified'),
          text('Multiplication needs nothing new either. Expand as usual, then replace i² wherever it appears:'),
          math('(2 + 3i)(1 - 4i) = 2 - 8i + 3i - 12i^{2} = 2 - 5i + 12 = 14 - 5i'),
          insight('Notice what happened at the last step. −12i² became +12, so a product of two complex numbers ended up with a larger real part than either started with. That sign change is where all the interesting behaviour comes from.'),
          text('Square roots of negatives now have answers. Pull out √(−1) first and the rest is an ordinary root:'),
          math('\\sqrt{-9} = \\sqrt{9}\\sqrt{-1} = 3i'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -5,
              max: 5,
              points: [
                { x: 3, y: 2, label: '3 + 2i' },
                { x: -4, y: 1, label: '−4 + i' },
                { x: 0, y: -3, label: '−3i' },
              ],
            },
            'Real part across, imaginary part up. A complex number needs two coordinates, which is exactly why its two parts cannot be added together.',
          ),
          tip('The picture answers the question students ask most about i: where is it? It is one unit up from zero, off the real line entirely. The real numbers are the horizontal axis of a plane you had not been shown.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve x² − 4x + 13 = 0.'),
          steps([
            { label: 'Use the quadratic formula.', latex: 'x = \\frac{4 \\pm \\sqrt{16 - 52}}{2}' },
            { label: 'The discriminant is negative.', latex: '16 - 52 = -36', note: 'Before this lesson, that was where the work stopped.' },
            { label: 'Take the root, using i.', latex: '\\sqrt{-36} = 6i' },
            { label: 'Finish.', latex: 'x = \\frac{4 \\pm 6i}{2} = 2 \\pm 3i' },
          ]),
          text('The two answers are 2 + 3i and 2 − 3i — the same except for the sign of the imaginary part. That pairing is not a coincidence of this example: whenever a quadratic with real coefficients has complex roots, they come in exactly such a pair.'),
          insight('So the discriminant no longer says "how many solutions". It says *what kind*: positive gives two real, zero gives one repeated, negative gives two complex. Every quadratic has two roots now, with no exceptions to remember.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('complex-numbers', 4)] },
      independent: { title: 'On your own', blocks: [practice('complex-numbers', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('complex-numbers', 3, 'Products, and reading the discriminant.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'i is defined by i² = −1, and obeys every other rule of algebra unchanged.',
            'A complex number is a + bi; the real and imaginary parts never combine.',
            'Multiply by expanding as usual, then replacing i² with −1.',
            '√(−k) is i√k — take out the √(−1) first.',
            'A negative discriminant means two complex roots, not none.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'polynomial-division-01',
    unitId: 'unit-complex-polynomials',
    conceptId: 'polynomial-division',
    title: 'Dividing Polynomials',
    summary: 'Long division for expressions — and the shortcut that turns a whole division into one substitution.',
    objectives: [
      'Divide one polynomial by another using long division',
      'Use the remainder theorem to find a remainder without dividing',
      'Use the factor theorem to factorise a cubic',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Factorising when you cannot see it',
        blocks: [
          text('Factorise x³ − 4x² + x + 6.'),
          text('None of the Algebra I methods apply. There is no common factor, it is not a difference of squares, and the pairs-of-numbers trick works on quadratics, not cubics.'),
          text('But if you happen to notice that x = 3 makes the whole thing zero, then (x − 3) must be one of its factors — and dividing by it turns the cubic into a quadratic you can already handle. This lesson is those two ideas: how to divide, and how to find the value to try.'),
        ],
      },
      intuition: {
        title: 'The same long division you already do',
        blocks: [
          text('Polynomial long division follows the arithmetic version step for step. At each stage you ask what the leading term must be multiplied by, subtract, and bring down the next term.'),
          warning('Every power must have a place, including the ones that are missing. To divide x³ − 7 you write x³ + 0x² + 0x − 7 — omitting the empty columns slides every later coefficient one place along, which is the single most common way this goes wrong.', 'Hold the empty places'),
          text('Division answers a question about a *remainder*, and there is a shortcut for that question alone. Dividing P(x) by (x − a) leaves the remainder P(a) — a whole long division replaced by one substitution:'),
          math('P(x) = (x - a)Q(x) + r \\quad\\Longrightarrow\\quad P(a) = 0 \\cdot Q(a) + r = r'),
          insight('The proof is the substitution itself. Put x = a into the identity and the quotient term is multiplied by zero, whatever the quotient happens to be. This is the **remainder theorem**.'),
          warning('Substitute the value that makes the divisor *zero*, not the number you can see. For x − 3 that is x = 3; for x + 3 it is x = −3. Set the bracket equal to zero and read the number off.', 'Which value to try'),
          text('The useful case is a remainder of nothing at all. If P(a) = 0 then the division is exact, so (x − a) is a factor — the **factor theorem**, and the way into a cubic:'),
          math('P(a) = 0 \\iff (x - a) \\text{ is a factor of } P(x)'),
          tip('To find a root worth trying, test the divisors of the constant term. For x³ − 4x² + x + 6 the constant is 6, so try ±1, ±2, ±3, ±6. Any whole-number root is somewhere in that short list, and nowhere else.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              roots: [-1, 2, 3],
              transform: { a: 0.5 },
              min: -3,
              max: 5,
              points: [
                { x: -1, y: 0, label: 'x = −1' },
                { x: 2, y: 0 },
                { x: 3, y: 0 },
              ],
              label: 'P(x)',
            },
            'The three roots of x³ − 4x² + x + 6, where the curve meets the axis (drawn with the vertical scale compressed, so all three fit). Each crossing is a value of a with P(a) = 0, and so a factor (x − a).',
          ),
          text('A root, a zero of the function, a crossing of the x-axis and a linear factor are four descriptions of one thing. The factor theorem is what lets you move between them.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Factorise x³ − 4x² + x + 6 completely.'),
          steps([
            { label: 'Try the divisors of 6. Start with x = 1.', latex: 'P(1) = 1 - 4 + 1 + 6 = 4', note: 'Not zero, so (x − 1) is not a factor.' },
            { label: 'Try x = −1.', latex: 'P(-1) = -1 - 4 - 1 + 6 = 0', note: 'Zero — so (x + 1) is a factor.' },
            { label: 'Divide by (x + 1).', latex: 'x^{3} - 4x^{2} + x + 6 = (x + 1)(x^{2} - 5x + 6)' },
            { label: 'Factorise the quadratic as usual.', latex: 'x^{2} - 5x + 6 = (x - 2)(x - 3)' },
            { label: 'Collect the three factors.', latex: '(x + 1)(x - 2)(x - 3)' },
          ]),
          text('Check by substituting: the roots are −1, 2 and 3, which are the three crossings in the figure above. A cubic gets you one division and then a quadratic — every degree you remove hands the problem back to something you can already do.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('polynomial-division', 4)] },
      independent: { title: 'On your own', blocks: [practice('polynomial-division', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('polynomial-division', 3, 'Remainders, and factorising a cubic.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Polynomial long division mirrors the arithmetic kind — and every missing power needs a 0 placeholder.',
            'Remainder theorem: dividing by (x − a) leaves P(a).',
            'Factor theorem: P(a) = 0 exactly when (x − a) is a factor.',
            'Substitute the value that makes the divisor zero — x + 3 means a = −3.',
            'Hunt for a first root among the divisors of the constant term.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'polynomial-functions-01',
    unitId: 'unit-complex-polynomials',
    conceptId: 'polynomial-functions',
    title: 'Polynomial Functions',
    summary: 'What a curve of degree three or more does at its ends, at its roots, and in between.',
    objectives: [
      'Predict end behaviour from the leading term',
      'Count roots with multiplicity, and say which are crossings',
      'Sketch a polynomial from its factored form',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Sketching without plotting',
        blocks: [
          text('Sketch y = (x + 2)(x − 1)²(x − 3).'),
          text('Plotting twenty points would take ten minutes and still leave you unsure what happens between them. Three questions do the job faster, and this lesson is those three: what the curve does far to the left and right, where it meets the axis, and what it does at each of those meetings.'),
        ],
      },
      intuition: {
        title: 'The ends, the roots, and the turns',
        blocks: [
          text('**The ends belong to the leading term.** At x = 100, x³ is ten thousand times as large as x, and a million times the constant. Everything except the highest power becomes invisible, so only that term decides which way each end goes.'),
          table(['Degree', 'Leading coefficient', 'Left end', 'Right end'], [
            ['even', 'positive', 'up', 'up'],
            ['even', 'negative', 'down', 'down'],
            ['odd', 'positive', 'down', 'up'],
            ['odd', 'negative', 'up', 'down'],
          ]),
          insight('Odd degrees have ends pointing opposite ways, which is why every odd polynomial has at least one real root: a curve running from below the axis to above it has to cross somewhere.'),
          text('**The roots are the factors.** Each factor (x − a) gives a root at x = a. Counted with repeats, a polynomial of degree n has exactly n roots — a statement that is only true because complex numbers exist, and which is false as stated over the reals.'),
          warning('A repeated factor is a repeated root, and it changes the shape. (x − 1)² gives the root 1 twice, and there the curve *touches* the axis and turns back instead of crossing. Odd multiplicity crosses; even multiplicity bounces.', 'Multiplicity is visible'),
          text('**The turns are limited.** A polynomial of degree n changes direction at most n − 1 times. A cubic has at most two turning points, so once you know its ends and its roots, there is very little room left for the curve to do anything unexpected.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quartic',
              transform: { a: 0.06, h: 0.5 },
              min: -4,
              max: 6,
              label: 'even degree',
            },
            'An even degree sends both ends the same way. A positive leading coefficient sends them both up.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'cubic',
              transform: { a: 0.12 },
              min: -4,
              max: 4,
              label: 'odd degree',
            },
            'An odd degree sends the ends opposite ways — which is why the curve is guaranteed to cross the axis at least once.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Sketch y = (x + 2)(x − 1)²(x − 3).'),
          steps([
            { label: 'Find the degree by counting factors.', latex: '1 + 2 + 1 = 4', note: 'Degree 4 — even.' },
            { label: 'The leading coefficient is the product of the leading coefficients.', latex: '+1', note: 'Even degree, positive: both ends go up.' },
            { label: 'Read the roots off the factors.', latex: 'x = -2,\\ 1\\ (\\text{twice}),\\ 3' },
            { label: 'Decide crossing or touching at each.', latex: '-2 \\text{ cross},\\ 1 \\text{ touch},\\ 3 \\text{ cross}' },
            { label: 'Find the y-intercept by putting x = 0.', latex: '(2)(1)(-3) = -6' },
          ]),
          text('That is enough to draw it: down from the upper left, crossing at −2, up to a turn, back down to touch the axis at 1, down through −6 on the way past, crossing again at 3, and away upwards. Four roots counted with multiplicity for a degree-four polynomial, exactly as promised.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('polynomial-functions', 4)] },
      independent: { title: 'On your own', blocks: [practice('polynomial-functions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('polynomial-functions', 3, 'End behaviour and multiplicity together.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Only the leading term decides the end behaviour.',
            'Even degree sends both ends the same way; odd degree sends them opposite ways.',
            'Degree n means n roots, counted with multiplicity and allowing complex ones.',
            'Odd multiplicity crosses the axis; even multiplicity touches and turns back.',
            'At most n − 1 turning points, so the shape is nearly forced.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'rational-functions-01',
    unitId: 'unit-rational-radical',
    conceptId: 'rational-functions',
    title: 'Rational Functions',
    summary: 'Curves with a forbidden input, and the lines they approach but never reach.',
    objectives: [
      'Find the domain of a rational function',
      'Tell a vertical asymptote from a hole',
      'Find the horizontal asymptote by comparing degrees',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The input that is not allowed',
        blocks: [
          text('A 300 km journey at v km/h takes 300 / v hours. Every speed gives a time — except one.'),
          text('At v = 0 there is no answer, and it is not that the answer is large. Standing still does not take a long time to travel 300 km; it never gets there at all. That single forbidden input is what makes a rational function behave differently from everything else in this course.'),
        ],
      },
      intuition: {
        title: 'Where the bottom is zero',
        blocks: [
          text('A **rational function** is one polynomial over another. Its domain is every real number except those making the denominator zero.'),
          math('f(x) = \\frac{P(x)}{Q(x)}, \\qquad Q(x) \\neq 0'),
          warning('Look at the *bottom* to find where the function breaks. Setting the top to zero finds the roots — the places the curve crosses the axis — which is a different question with a different answer.', 'Top for roots, bottom for breaks'),
          text('Near a forbidden input the function grows without limit, hugging a vertical line it never touches. That line is a **vertical asymptote**. Approaching from one side sends the curve up and from the other side down, which is why the two halves of the graph never join.'),
          text('Not every zero of the original denominator is an asymptote, though. Factorise first:'),
          math('\\frac{(x - 2)(x + 1)}{(x - 2)(x - 3)} = \\frac{x + 1}{x - 3}, \\quad x \\neq 2'),
          insight('The (x − 2) cancels, so nothing dramatic happens at x = 2 — the curve carries on as though the point were there, with a single point missing. That is a **hole**, not an asymptote. The input is still forbidden, because the original expression was undefined there; the graph just has a gap the size of one point.'),
          text('Far from the origin the leading terms take over again, exactly as they did for polynomials, and the curve settles towards a **horizontal asymptote**:'),
          table(['Degrees', 'Behaviour far out', 'Horizontal asymptote'], [
            ['top < bottom', 'the bottom wins', 'y = 0'],
            ['top = bottom', 'they balance', 'y = ratio of leading coefficients'],
            ['top > bottom', 'the top wins', 'none — it runs away'],
          ]),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'reciprocal',
              transform: { h: 2, k: 1 },
              asymptotes: { vertical: [2], horizontal: [1] },
              min: -4,
              max: 6,
              label: 'y = 1/(x−2) + 1',
            },
            'The dashed lines are approached and never met. The curve comes arbitrarily close to x = 2 without ever arriving, which is what "undefined" looks like.',
          ),
          tip('Notice the two branches never join. There is no way to draw this curve without lifting your pen, and the asymptote is the reason.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Describe the graph of f(x) = (x² − x − 6) / (x² − 9).'),
          steps([
            { label: 'Factorise the top.', latex: 'x^{2} - x - 6 = (x - 3)(x + 2)' },
            { label: 'Factorise the bottom.', latex: 'x^{2} - 9 = (x - 3)(x + 3)' },
            { label: 'Note the domain before cancelling anything.', latex: 'x \\neq 3, \\; x \\neq -3' },
            { label: 'Cancel the shared factor.', latex: 'f(x) = \\frac{x + 2}{x + 3}' },
            { label: 'The cancelled factor leaves a hole.', latex: '\\text{hole at } x = 3' },
            { label: 'The surviving factor gives an asymptote.', latex: 'x = -3' },
            { label: 'Equal degrees, so compare leading coefficients.', latex: 'y = 1' },
          ]),
          text('Two forbidden inputs, and they behave completely differently: at x = −3 the curve runs off to infinity, and at x = 3 there is one missing point you would need a magnifying glass to see. Cancelling before checking the domain would have lost the second one entirely.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('rational-functions', 4)] },
      independent: { title: 'On your own', blocks: [practice('rational-functions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('rational-functions', 3, 'Holes, asymptotes and end behaviour together.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The domain excludes every zero of the denominator — check before cancelling.',
            'A surviving zero of the bottom is a vertical asymptote; a cancelled one is a hole.',
            'Zeros of the top are the roots, not the breaks.',
            'Compare degrees for the horizontal asymptote: lower top gives y = 0, equal degrees give the ratio.',
            'An asymptote is approached, never reached.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'radical-equations-01',
    unitId: 'unit-rational-radical',
    conceptId: 'radical-equations',
    title: 'Radical Equations',
    summary: 'Squaring both sides solves the equation and invents answers — so checking stops being optional.',
    objectives: [
      'Solve an equation containing a square root',
      'Isolate the radical before squaring',
      'Identify and discard extraneous solutions',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'An answer that is not one',
        blocks: [
          text('Solve √x = −3.'),
          text('Square both sides and you get x = 9, which looks like an answer. Put it back into the question, though: √9 is 3, and 3 is not −3.'),
          text('The equation has no solution at all, and the method produced one anyway. This is the only place in school algebra where a correct step can manufacture a wrong answer, and knowing why is the whole lesson.'),
        ],
      },
      intuition: {
        title: 'Why squaring loses information',
        blocks: [
          text('Squaring is not reversible. 2 and −2 both square to 4, so squaring both sides of an equation throws away the sign — and once thrown away, it cannot be recovered:'),
          math('2 = -2 \\;\\text{ is false} \\qquad 2^{2} = (-2)^{2} \\;\\text{ is true}'),
          insight('So squaring can turn a false statement into a true one. Every solution of the original equation still solves the squared one, but the squared one may have picked up extras that never solved anything. Those are **extraneous solutions**.'),
          warning('This makes checking part of the method, not a tidy habit at the end. Substitute every answer into the *original* equation — the squared version will happily accept the impostors, because it is the version that created them.', 'Checking is a step, not a courtesy'),
          text('One more rule of order. Squaring only removes a root that stands alone:'),
          math('(\\sqrt{x} + 3)^{2} = x + 6\\sqrt{x} + 9'),
          text('The root is still there, and now there is more of it. Get the radical by itself on one side first, and then square.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'sqrt',
              min: -2,
              max: 8,
              points: [{ x: 4, y: 2, label: '√4 = 2' }],
              label: 'y = √x',
            },
            'The square root curve never dips below the axis. There is nowhere on it with a negative output, which is why √x = −3 was hopeless from the start.',
          ),
          tip('A graph settles most extraneous-solution questions before any algebra. Solutions are where two curves meet; if one curve lives entirely above the axis and the other entirely below it, they meet nowhere.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve √(x + 6) = x.'),
          steps([
            { label: 'The radical is already alone, so square both sides.', latex: 'x + 6 = x^{2}' },
            { label: 'Rearrange into a quadratic.', latex: 'x^{2} - x - 6 = 0' },
            { label: 'Factorise.', latex: '(x - 3)(x + 2) = 0' },
            { label: 'Two candidates.', latex: 'x = 3 \\text{ or } x = -2', note: 'Candidates, not yet answers.' },
            { label: 'Check x = 3 in the original.', latex: '\\sqrt{9} = 3 \\;\\checkmark' },
            { label: 'Check x = −2 in the original.', latex: '\\sqrt{4} = 2 \\neq -2 \\;\\times' },
          ]),
          text('The answer is x = 3 alone. The −2 is not a mistake in the algebra — every step was valid — it is an artefact of the squaring, and the check is the only thing that catches it.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('radical-equations', 4)] },
      independent: { title: 'On your own', blocks: [practice('radical-equations', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('radical-equations', 3, 'Equations where one candidate has to be discarded.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Isolate the radical before squaring — squaring a sum leaves a root behind.',
            'Squaring can create solutions that never solved the original.',
            'Check every candidate in the original equation, and discard the ones that fail.',
            'A square root output is never negative, so √x = −k has no solutions.',
            'Getting no valid answers is a legitimate result, not a sign of an error.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'rational-exponents-01',
    unitId: 'unit-rational-radical',
    conceptId: 'rational-exponents',
    title: 'Rational Exponents',
    summary: 'What x to the power of one half must mean, once you insist the exponent rules keep working.',
    objectives: [
      'Convert between radical form and rational-exponent form',
      'Evaluate powers with fractional and negative fractional exponents',
      'Apply the exponent rules unchanged when the exponents are fractions',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'A power that cannot be a repeated multiplication',
        blocks: [
          text('An exponent has meant "how many copies to multiply" since you first met one. So this question looks like nonsense:'),
          math('9^{\\frac{1}{2}} = \\;?'),
          text('Half a copy of 9 is not a thing. There is nothing to count.'),
          insight('This is the same difficulty as 2⁰ and 2^(−3), and it was settled the same way. Nobody discovered what a zero exponent "really means". They asked what value would let the existing rules go on working, found there was only one answer, and adopted it.'),
        ],
      },
      intuition: {
        title: 'Only one value can work',
        blocks: [
          text('The rule that decides it is the one for multiplying powers: add the exponents.'),
          math('9^{\\frac{1}{2}} \\cdot 9^{\\frac{1}{2}} = 9^{\\frac{1}{2} + \\frac{1}{2}} = 9^{1} = 9'),
          text('So 9^(1/2) is a number that gives 9 when multiplied by itself. That is the square root, and there is no choice about it.'),
          insight('An exponent of 1/n is the n-th root. Not by analogy or convention — it is forced, because any other value would break a rule that already held for every whole-number exponent.'),
          text('The general form follows from the power-of-a-power rule:'),
          math('x^{\\frac{m}{n}} = \\sqrt[n]{x^{m}} = \\left(\\sqrt[n]{x}\\right)^{m}'),
          warning('The **denominator** is the root and the **numerator** is the power. 8^(2/3) is the *cube* root of 8, squared — the 3 is underneath in the fraction and underneath in the radical. Swapping them turns 4 into 22.6.', 'Bottom means root'),
          text('The two orders in that identity give the same answer, but not the same arithmetic. Take the root first:'),
          table(['Route', 'Working', 'Difficulty'], [
            ['Root first', '∛8 = 2, then 2² = 4', 'small numbers throughout'],
            ['Power first', '8² = 64, then ∛64 = 4', 'needs the cube root of 64'],
          ]),
          text('A negative rational exponent stacks the two ideas that are already there — flip, then root and power:'),
          math('16^{-\\frac{3}{2}} = \\frac{1}{16^{\\frac{3}{2}}} = \\frac{1}{4^{3}} = \\frac{1}{64}'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'function-graph', curve: 'sqrt', min: -1, max: 6, points: [{ x: 4, y: 2, label: '4^½ = 2' }] },
            'y = x^(1/2) is the square-root curve. Writing the root as an exponent is not new notation for its own sake: it puts this curve into the same family as y = x², y = x³ and y = x^(−1), where one set of rules covers all of them.',
          ),
          tip('It also explains the domain. x^(1/2) needs a square root, so negative inputs are out — which is why the curve starts at the origin and goes only one way.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate 32^(3/5).'),
          steps([
            { label: 'Read the denominator as a root.', latex: '\\sqrt[5]{32} = 2', note: '2⁵ = 32.' },
            { label: 'Read the numerator as a power.', latex: '2^{3} = 8' },
            { label: 'So the answer is 8.', latex: '32^{\\frac{3}{5}} = 8' },
          ]),
          text('Now the same expression with a minus sign: 32^(−3/5).'),
          steps([
            { label: 'The minus flips it.', latex: '\\frac{1}{32^{\\frac{3}{5}}}' },
            { label: 'The bottom is the number just found.', latex: '\\frac{1}{8}' },
          ]),
          insight('Notice what the minus sign did not do. It did not make the answer negative. −3/5 as an exponent means "one over", and it says nothing at all about the sign of the result.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('rational-exponents', 4)] },
      independent: { title: 'On your own', blocks: [practice('rational-exponents', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('rational-exponents', 3, 'Negative exponents, and products where the exponents must be added.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'x^(1/n) is the n-th root of x — forced by the rule that powers of the same base add.',
            'x^(m/n) is the n-th root of x^m: denominator underneath, numerator on top.',
            'Take the root first. The arithmetic is smaller and the answer is the same.',
            'A negative exponent means a reciprocal, never a negative answer.',
            'Every exponent rule you know applies unchanged to fractional exponents.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'rational-equations-01',
    unitId: 'unit-rational-radical',
    conceptId: 'rational-equations',
    title: 'Solving Rational Equations',
    summary: 'Clearing the denominators, and the answer that has to be thrown away because it was never allowed.',
    objectives: [
      'Clear denominators by multiplying every term by a common denominator',
      'Identify the values excluded from the domain before solving',
      'Check candidate solutions and discard the ones that were never permitted',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'An equation with a hole in it',
        blocks: [
          text('Solve this:'),
          math('\\frac{x^{2}}{x - 3} = \\frac{9}{x - 3}'),
          text('The denominators are identical, so the numerators must match: x² = 9, giving x = 3 or x = −3. Two answers, quickly found.'),
          warning('Substitute x = 3 back into the question. Both sides become 9/0, which is not a number at all. The original equation has nothing to say at x = 3 — so 3 cannot be a solution to it, however neatly it solved the equation you wrote next.', 'One of them is not an answer'),
          text('This is not bad luck with the numbers. Every rational equation carries values it is not defined at, and clearing the denominators quietly throws that information away.'),
        ],
      },
      intuition: {
        title: 'Clear the fractions, then check the domain',
        blocks: [
          text('The method has two halves, and the second is not optional.'),
          list([
            'Write down the values that make any denominator zero. Do this *first*, before any algebra — they are properties of the question, not of your working.',
            'Multiply every term on both sides by a common denominator.',
            'Solve the equation that results, which has no fractions in it.',
            'Discard any answer that appears on the list from step one.',
          ], true),
          insight('The same thing happened with radical equations: squaring both sides can produce answers the original never had. Multiplying by a variable expression is the same kind of move — for one particular value of x you multiplied by zero, and multiplying an equation by zero makes anything true.'),
          text('Step two is where the other common error lives. "Multiply every term" means every term:'),
          math('\\frac{x}{4} + \\frac{x}{6} = 5 \\;\\longrightarrow\\; 3x + 2x = 60'),
          warning('The 5 on the right is a term too. Multiplying only the fractions gives 3x + 2x = 5 and an answer twelve times too small. Denominators are not crossed out; the whole equation is scaled up.', 'The term with no fraction still gets multiplied'),
          text('And cross-multiplication is narrower than it looks. It is a shortcut for one fraction equal to one fraction, and nothing else:'),
          table(['Equation', 'Cross-multiply?', 'Why'], [
            ['a/b = c/d', 'yes', 'one fraction each side'],
            ['a/x + 1/3 = 2', 'no', 'the left is a sum, not a fraction'],
            ['a/x = c', 'yes', 'c is c/1'],
          ]),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'reciprocal',
              transform: { h: 2 },
              asymptotes: { vertical: [2] },
              lines: [{ slope: 0, intercept: 1, dashed: true }],
              min: -4,
              max: 6,
            },
            'y = 1/(x − 2), with the line y = 1 across it. Solving 1/(x − 2) = 1 means finding where the curve meets the line — and the picture shows why x = 2 can never be an answer to anything: the curve is not there.',
          ),
          tip('An excluded value is a gap in the graph, not a point that happens to be wrong. That is why checking is a domain question rather than an arithmetic one.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve x/(x − 4) = 2 + 4/(x − 4).'),
          steps([
            { label: 'What is excluded?', latex: 'x \\ne 4', note: 'Written down before anything else.' },
            { label: 'Multiply every term by x − 4.', latex: 'x = 2(x - 4) + 4' },
            { label: 'Expand and collect.', latex: 'x = 2x - 4' },
            { label: 'Solve.', latex: 'x = 4' },
            { label: 'Check against the excluded list.', latex: 'x = 4 \\;\\times' },
          ]),
          text('The only candidate is the one value the equation was never allowed to take. So this equation has **no solutions** — and that is a complete, correct answer, not a failure to find one.'),
          insight('A student who skips the last line reports x = 4 and is confidently wrong. A student who never wrote the excluded value down has nothing to check against. The two halves of the method protect each other.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('rational-equations', 4)] },
      independent: { title: 'On your own', blocks: [practice('rational-equations', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('rational-equations', 3, 'Equations where a candidate has to be discarded, or where none survives.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'List the values that make a denominator zero before you start solving.',
            'Multiply *every* term by the common denominator, including terms with no fraction.',
            'Cross-multiplication needs a single fraction on each side.',
            'Check every candidate against the excluded list — multiplying up can invent answers.',
            '"No solution" is an answer.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'polynomial-inequalities-01',
    unitId: 'unit-systems-inequalities',
    conceptId: 'polynomial-inequalities',
    title: 'Polynomial and Rational Inequalities',
    summary: 'When the answer is not a number but a stretch of the number line — and how a graph hands it to you.',
    objectives: [
      'Solve a factored polynomial inequality by testing the intervals its roots create',
      'Read a solution set off a graph as the intervals where the curve is on one side of the axis',
      'Explain why multiplying an inequality by a variable expression is unsafe',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The answer stops being a number',
        blocks: [
          text('You can already solve x² − x − 6 = 0. Factorise, and the roots are 3 and −2.'),
          text('Now the same expression with a different symbol:'),
          math('x^{2} - x - 6 < 0'),
          text('The roots 3 and −2 are exactly the two values that make it **equal** to zero — the only values that are certainly *not* in the answer. They are the edges of the solution, not the solution.'),
          insight('This is the first time in algebra that the answer is a region rather than a list. Reporting "x = 3 or x = −2" here is not a small slip: it is answering a different question, the one with an equals sign.'),
        ],
      },
      intuition: {
        title: 'Roots cut the line into stretches',
        blocks: [
          text('A polynomial can only change sign by passing through zero. So between two consecutive roots its sign never changes — the whole stretch is positive or the whole stretch is negative, and one test point settles which.'),
          list([
            'Get everything on one side, so the other side is 0.',
            'Factorise, and find the roots. They cut the line into intervals.',
            'Test one convenient number from each interval.',
            'Collect the intervals whose sign matches the inequality.',
          ], true),
          text('For (x + 2)(x − 3) < 0 the roots are −2 and 3, and there are three stretches to test:'),
          table(['Interval', 'Test', '(x + 2)', '(x − 3)', 'Product'], [
            ['x < −2', 'x = −3', 'negative', 'negative', 'positive'],
            ['−2 < x < 3', 'x = 0', 'positive', 'negative', 'negative'],
            ['x > 3', 'x = 4', 'positive', 'positive', 'positive'],
          ]),
          text('Only the middle row is negative, so the solution is −2 < x < 3.'),
          insight('The signs need not alternate. A repeated factor such as (x − 1)² is never negative, so the curve touches the axis there and comes back on the same side. That is why the method is to *test* each interval rather than to assume they take turns.'),
          warning('A rational inequality such as (x + 1)/(x − 3) < 2 must not be cleared by multiplying both sides by x − 3. That expression is negative for x < 3 and positive for x > 3, and multiplying an inequality by a negative reverses it — so the move is right on half the line and wrong on the other half. Subtract 2, combine into a single fraction, and read the sign of the whole thing instead.', 'Never multiply by something whose sign you do not know'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'function-graph', roots: [-2, 1, 3], min: -4, max: 4 },
            'y = (x + 2)(x − 1)(x − 3). "Where is this negative?" is "where is the curve below the axis?" — the two stretches on the left of each dip. The roots are where it crosses, which is exactly where the answer changes.',
          ),
          tip('If you can sketch the curve, you have solved every inequality it can appear in. Reading a picture is faster and safer than manipulating an inequality symbol, and it is the same skill that end-behaviour and multiplicity were building.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve x³ − 4x ≥ 0.'),
          steps([
            { label: 'One side is already 0. Factorise.', latex: 'x(x - 2)(x + 2) \\ge 0' },
            { label: 'The roots cut the line at three places.', latex: 'x = -2,\\; 0,\\; 2' },
            { label: 'Test x = −3.', latex: '(-3)(-5)(-1) = -15', note: 'Negative — not in the solution.' },
            { label: 'Test x = −1.', latex: '(-1)(-3)(1) = 3', note: 'Positive — in it.' },
            { label: 'Test x = 1.', latex: '(1)(-1)(3) = -3', note: 'Negative — out.' },
            { label: 'Test x = 3.', latex: '(3)(1)(5) = 15', note: 'Positive — in it.' },
          ]),
          text('So the solution is −2 ≤ x ≤ 0 or x ≥ 2. The sign ≥ includes the roots themselves, which is why those ends are closed.'),
          insight('Four test points, no algebra with the inequality symbol at all. The only place the symbol mattered was the last sentence — deciding whether the endpoints are in.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('polynomial-inequalities', 4)] },
      independent: { title: 'On your own', blocks: [practice('polynomial-inequalities', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('polynomial-inequalities', 3, 'Cubics, and inequalities with a variable in the denominator.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Get everything to one side first, so you are asking about the sign of one expression.',
            'The roots are the boundaries of the answer, not the answer.',
            'Test one point per interval. Never assume the signs alternate.',
            '≤ and ≥ include the roots; < and > do not.',
            'Never multiply an inequality by an expression whose sign you do not know.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'nonlinear-systems-01',
    unitId: 'unit-systems-inequalities',
    conceptId: 'nonlinear-systems',
    title: 'Systems With a Curve',
    summary: 'Two equations where one of them bends — so the answer is two points, or one, or none at all.',
    objectives: [
      'Solve a system of a line and a curve by substitution',
      'Use the discriminant to count intersections without finding them',
      'Explain why elimination fails when one equation is not linear',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two straight lines had one answer',
        blocks: [
          text('Every system you have solved so far was two straight lines, and the picture was always the same: they crossed once, or they were parallel, or they were the same line. One answer, none, or all of them.'),
          text('Replace one of the lines with a parabola and the geography changes:'),
          math('y = x^{2} \\qquad y = x + 6'),
          text('A straight line can cut a parabola twice. Two solutions is now the ordinary case, and the count is part of what the question asks.'),
        ],
      },
      intuition: {
        title: 'Substitution still works; elimination does not',
        blocks: [
          text('Both equations give y on its own, so wherever the graphs agree the two right-hand sides are equal:'),
          math('x^{2} = x + 6 \\;\\longrightarrow\\; x^{2} - x - 6 = 0 \\;\\longrightarrow\\; (x - 3)(x + 2) = 0'),
          text('So x = 3 or x = −2, and putting each back into the line gives y = 9 and y = 4. The solutions are the points (3, 9) and (−2, 4).'),
          warning('Elimination has nothing to cancel here. Subtracting the two equations removes y, which is what you wanted, but there is no x² in the line to cancel the x² in the curve — so what is left is still quadratic. Elimination cancels *matching* terms, and a curve and a line do not match.', 'Subtracting will not remove the bend'),
          insight('Two answers is the normal outcome, and both are answers. Finding x = 3 and stopping is the single most common way to lose half the marks on this topic — the quadratic told you there was another root and it was ignored.'),
          text('Sometimes you only need the count, and then the discriminant is enough:'),
          table(['Discriminant of the resulting quadratic', 'Picture', 'Solutions'], [
            ['positive', 'the line cuts the curve', '2'],
            ['zero', 'the line is a tangent', '1'],
            ['negative', 'the line misses entirely', '0'],
          ]),
          text('A line and a circle behave the same way, and a circle makes the count obvious: a straight line through a circle either cuts it twice, grazes it once, or passes it by.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              lines: [{ slope: 1, intercept: 6 }],
              points: [
                { x: 3, y: 9, label: '(3, 9)' },
                { x: -2, y: 4, label: '(−2, 4)' },
              ],
              min: -5,
              max: 10,
            },
            'y = x² and y = x + 6. The solutions of the system are precisely the points the two graphs share — which is what makes "two solutions" something you can see rather than something to be told.',
          ),
          tip('Slide the line up or down in your head. At some height it stops cutting and starts touching, and just above that it misses altogether. That moment of touching is the discriminant hitting zero.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve the system x² + y² = 25 and y = x + 1.'),
          steps([
            { label: 'Substitute the line into the circle.', latex: 'x^{2} + (x + 1)^{2} = 25' },
            { label: 'Expand.', latex: '2x^{2} + 2x + 1 = 25' },
            { label: 'Tidy into standard form.', latex: 'x^{2} + x - 12 = 0' },
            { label: 'Factorise.', latex: '(x + 4)(x - 3) = 0' },
            { label: 'Two x values, each with its own y.', latex: '(-4, -3) \\text{ and } (3, 4)' },
          ]),
          insight('The last step is where points get lost. Each x must be taken back to the line to collect its partner — an x on its own is half an answer, and the two y values are different.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('nonlinear-systems', 4)] },
      independent: { title: 'On your own', blocks: [practice('nonlinear-systems', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('nonlinear-systems', 3, 'Counting intersections, and systems with a circle.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Substitute — do not eliminate. There is no x² in the line to cancel against.',
            'Setting the two right-hand sides equal produces a quadratic; its roots are the x-coordinates.',
            'Every root gives a point. Take each x back to an equation for its y.',
            'The discriminant counts the intersections: positive two, zero one, negative none.',
            'One intersection means the line is a tangent, not that you made a mistake.',
          ]),
        ],
      },
    },
  }),
];
